import React, { useState } from 'react';
import { Form, Input, Button, Typography, Alert, Space } from 'antd';
import { LockOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const { Title, Text } = Typography;

const _containerStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background:
    'radial-gradient(circle at 20% 0%, rgba(99,102,241,0.35) 0%, transparent 45%), radial-gradient(circle at 80% 80%, rgba(14,165,233,0.3) 0%, transparent 50%), linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
  padding: 24,
  position: 'relative',
  overflow: 'hidden',
};

const _cardStyle: React.CSSProperties = {
  width: 420,
  borderRadius: 18,
  background: 'rgba(255,255,255,0.98)',
  boxShadow: '0 20px 60px rgba(15,23,42,0.45)',
  padding: 40,
  position: 'relative',
  backdropFilter: 'blur(12px)',
};

interface ResetFormValues {
  new_password: string;
  confirm_password: string;
}

export function ResetPasswordPage(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const token = searchParams.get('token') ?? '';

  const onFinish = async ({ new_password }: ResetFormValues): Promise<void> => {
    if (!token) {
      setError('Kein Reset-Token in der URL gefunden.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await axios.post('/api/v1/auth/password/reset', { token, new_password });
      setDone(true);
      // Nach 3 Sekunden zum Login weiterleiten
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    } catch (err: unknown) {
      let message = 'Passwort konnte nicht zurückgesetzt werden.';
      if (
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof (err as { response?: { data?: { detail?: string } } }).response?.data?.detail === 'string'
      ) {
        message = (err as { response: { data: { detail: string } } }).response.data.detail;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={_containerStyle}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          pointerEvents: 'none',
        }}
      />
      <div style={_cardStyle}>
        <Space direction='vertical' size={24} style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: 56,
                height: 56,
                margin: '0 auto 12px',
                borderRadius: 14,
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 28,
                boxShadow: '0 10px 30px rgba(99,102,241,0.4)',
              }}
            >
              <ThunderboltOutlined />
            </div>
            <Title level={3} style={{ marginBottom: 4 }}>
              Neues Passwort festlegen
            </Title>
            <Text type='secondary'>Mindestens 12 Zeichen. Der Link ist einmalig verwendbar.</Text>
          </div>

          {error && (
            <Alert message={error} type='error' showIcon closable onClose={() => setError(null)} style={{ borderRadius: 10 }} />
          )}

          {done ? (
            <Alert
              message='Passwort erfolgreich zurückgesetzt. Du wirst gleich zur Anmeldung weitergeleitet…'
              type='success'
              showIcon
              style={{ borderRadius: 10 }}
            />
          ) : !token ? (
            <Alert
              message='Ungültiger Reset-Link. Bitte fordere einen neuen an.'
              type='error'
              showIcon
              style={{ borderRadius: 10 }}
            />
          ) : (
            <Form layout='vertical' onFinish={onFinish} autoComplete='off' size='large'>
              <Form.Item
                name='new_password'
                label='Neues Passwort'
                rules={[
                  { required: true, message: 'Passwort eingeben' },
                  { min: 12, message: 'Mindestens 12 Zeichen' },
                ]}
                hasFeedback
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: 'rgba(15,23,42,0.4)' }} />}
                  placeholder='Mindestens 12 Zeichen'
                />
              </Form.Item>

              <Form.Item
                name='confirm_password'
                label='Passwort bestätigen'
                dependencies={['new_password']}
                rules={[
                  { required: true, message: 'Passwort bestätigen' },
                  ({ getFieldValue }) => ({
                    validator(_, value: string) {
                      if (!value || getFieldValue('new_password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwörter stimmen nicht überein'));
                    },
                  }),
                ]}
                hasFeedback
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: 'rgba(15,23,42,0.4)' }} />}
                  placeholder='Passwort wiederholen'
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type='primary'
                  htmlType='submit'
                  loading={loading}
                  block
                  size='large'
                  style={{
                    height: 44,
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    border: 'none',
                    boxShadow: '0 4px 16px rgba(99,102,241,0.35)',
                  }}
                >
                  Passwort speichern
                </Button>
              </Form.Item>
            </Form>
          )}

          <div style={{ textAlign: 'center' }}>
            <Link to='/login' style={{ color: '#6366f1', fontSize: 14 }}>
              ← Zurück zur Anmeldung
            </Link>
          </div>

          <div style={{ textAlign: 'center' }}>
            <Text type='secondary' style={{ fontSize: 12 }}>
              © {new Date().getFullYear()} Schnitzraum · sicher und DSGVO-konform
            </Text>
          </div>
        </Space>
      </div>
    </div>
  );
}
